import './QuizEditor.css';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { searchQuestions, getQuestionById } from '../../services/questionService';
import { searchAuthors } from '../../services/authorService';
import { searchBooks } from '../../services/bookService';
import { fetchQuiz, updateQuiz } from '../../services/quizService';
import CreateQuestionModal from './CreateQuestionModal';

const QUESTION_TYPE_LABELS = {
    'MULTICHOICE': 'Výběr z možností',
    'MULTIRESPONSE': 'Více správných odpovědí',
    'FILLIN': 'Doplňovačka',
    'FILL_IN': 'Doplňovačka',
    'LINKING': 'Propojovačka',
    'TIMELINE': 'Časová osa',
    'FINDINTEXT': 'Najít v textu',
    'FIND_IN_TEXT': 'Najít v textu',
};

function EditQuiz() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [title, setTitle] = useState('');
    const [shortDescription, setShortDescription] = useState('');
    const [description, setDescription] = useState('');
    const [tags, setTags] = useState([]);
    const [tagInput, setTagInput] = useState('');
    const [loadingQuiz, setLoadingQuiz] = useState(true);
    const [loadError, setLoadError] = useState(null);

    // Entity search state (author/book)
    const [entitySearchTerm, setEntitySearchTerm] = useState('');
    const [debouncedEntitySearch, setDebouncedEntitySearch] = useState('');
    const [entitySearchResults, setEntitySearchResults] = useState({ books: [], authors: [] });
    const [entitySearching, setEntitySearching] = useState(false);
    const [selectedEntityFilters, setSelectedEntityFilters] = useState([]);

    // Question results
    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [selectedQuestions, setSelectedQuestions] = useState([]);
    const [showCreateQuestion, setShowCreateQuestion] = useState(false);

    // Load existing quiz data
    useEffect(() => {
        const loadQuiz = async () => {
            try {
                setLoadingQuiz(true);
                setLoadError(null);
                const quiz = await fetchQuiz(id);
                setTitle(quiz.title || '');
                setShortDescription(quiz.shortDescription || '');
                setDescription(quiz.description || '');
                setTags(quiz.tags || []);
                if (quiz.questionIds && Array.isArray(quiz.questionIds) && quiz.questionIds.length > 0) {
                    const questions = await Promise.all(
                        quiz.questionIds.map(qId => getQuestionById(qId).catch(() => null))
                    );
                    setSelectedQuestions(questions.filter(q => q !== null));
                }
            } catch (err) {
                console.error('Error loading quiz:', err);
                setLoadError(err.message);
            } finally {
                setLoadingQuiz(false);
            }
        };
        loadQuiz();
    }, [id]);

    // Debounce entity search
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedEntitySearch(entitySearchTerm);
        }, 500);
        return () => clearTimeout(timer);
    }, [entitySearchTerm]);

    // Search both books and authors
    useEffect(() => {
        const performSearch = async () => {
            if (debouncedEntitySearch.trim().length < 2) {
                setEntitySearchResults({ books: [], authors: [], showUnlinked: false });
                return;
            }
            try {
                setEntitySearching(true);
                const term = debouncedEntitySearch.trim();
                const bookFilters = { title: term, authorName: term };
                const [bookResults, authorResults] = await Promise.all([
                    searchBooks(bookFilters, 0, 5),
                    searchAuthors(term, 0, 5)
                ]);
                const showUnlinked = 'literární teorie'.includes(term.toLowerCase());
                setEntitySearchResults({
                    books: bookResults.content || [],
                    authors: authorResults.content || [],
                    showUnlinked
                });
            } catch (err) {
                console.error('Error searching:', err);
                setEntitySearchResults({ books: [], authors: [], showUnlinked: false });
            } finally {
                setEntitySearching(false);
            }
        };
        performSearch();
    }, [debouncedEntitySearch]);

    const handleSelectBook = (book) => {
        const alreadySelected = selectedEntityFilters.some(
            item => item.type === 'book' && item.data.id === book.id
        );
        if (alreadySelected) return;
        setSelectedEntityFilters(prev => [...prev, { type: 'book', data: book }]);
        setEntitySearchTerm('');
        setEntitySearchResults({ books: [], authors: [], showUnlinked: false });
    };

    const handleSelectAuthor = (author) => {
        const alreadySelected = selectedEntityFilters.some(
            item => item.type === 'author' && item.data.id === author.id
        );
        if (alreadySelected) return;
        setSelectedEntityFilters(prev => [...prev, { type: 'author', data: author }]);
        setEntitySearchTerm('');
        setEntitySearchResults({ books: [], authors: [], showUnlinked: false });
    };

    const handleSelectUnlinked = () => {
        const alreadySelected = selectedEntityFilters.some(item => item.type === 'unlinked');
        if (alreadySelected) return;
        setSelectedEntityFilters(prev => [...prev, { type: 'unlinked' }]);
        setEntitySearchTerm('');
        setEntitySearchResults({ books: [], authors: [], showUnlinked: false });
    };

    const handleRemoveEntityFilter = (index) => {
        setSelectedEntityFilters(prev => prev.filter((_, i) => i !== index));
    };

    // Search questions when entity filters change
    const loadQuestions = useCallback(async () => {
        const selectedAuthor = selectedEntityFilters.find(f => f.type === 'author');
        const selectedBook = selectedEntityFilters.find(f => f.type === 'book');
        const selectedUnlinked = selectedEntityFilters.some(f => f.type === 'unlinked');
        if (!selectedAuthor && !selectedBook && !selectedUnlinked) {
            setSearchResults([]);
            return;
        }
        try {
            setSearchLoading(true);
            const data = await searchQuestions({
                authorId: selectedAuthor?.data.id,
                readingJournalId: selectedBook?.data.id,
                unlinked: selectedUnlinked || undefined,
                page: 0,
                size: 50
            });
            setSearchResults(data.content || (Array.isArray(data) ? data : []));
        } catch (err) {
            console.error('Error searching questions:', err);
        } finally {
            setSearchLoading(false);
        }
    }, [selectedEntityFilters]);

    useEffect(() => {
        loadQuestions();
    }, [loadQuestions]);

    const toggleQuestion = (question) => {
        setSelectedQuestions(prev => {
            const exists = prev.find(q => q.id === question.id);
            if (exists) {
                return prev.filter(q => q.id !== question.id);
            }
            return [...prev, question];
        });
    };

    const removeSelectedQuestion = (questionId) => {
        setSelectedQuestions(prev => prev.filter(q => q.id !== questionId));
    };

    const handleTagKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            const newTag = tagInput.trim().replace(/,/g, '');
            if (newTag && !tags.includes(newTag)) {
                setTags([...tags, newTag]);
            }
            setTagInput('');
        }
    };

    const removeTag = (tagToRemove) => {
        setTags(tags.filter(tag => tag !== tagToRemove));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!title.trim()) {
            alert('Vyplňte název kvízu.');
            return;
        }
        try {
            await updateQuiz(id, {
                title: title.trim(),
                shortDescription: shortDescription.trim(),
                description: description.trim(),
                tags,
                questionIds: selectedQuestions.map(q => q.id),
            });
            navigate('/editor-kvizu');
        } catch (err) {
            console.error('Error updating quiz:', err);
            alert('Nepodařilo se aktualizovat kvíz: ' + err.message);
        }
    };

    const handleCancel = () => {
        navigate('/editor-kvizu/manage');
    };

    const handleQuestionCreated = (createdQuestion) => {
        setSelectedQuestions(prev => {
            const exists = prev.find(q => q.id === createdQuestion.id);
            if (exists) return prev;
            return [...prev, createdQuestion];
        });
        setShowCreateQuestion(false);
    };

    if (loadingQuiz) {
        return (
            <div className="quiz-editor-page">
                <div className="quiz-editor-container">
                    <p className="page-subtitle">Načítání kvízu...</p>
                </div>
            </div>
        );
    }

    if (loadError) {
        return (
            <div className="quiz-editor-page">
                <div className="quiz-editor-container">
                    <button className="back-button" onClick={() => navigate('/editor-kvizu/manage')}>
                        ← Zpět
                    </button>
                    <p className="page-subtitle" style={{ color: '#e94560' }}>{loadError}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="quiz-editor-page">
            <div className="quiz-editor-container">
                <button className="back-button" onClick={() => navigate('/editor-kvizu/manage')}>
                    ← Zpět
                </button>
                <h1 className="page-title">Upravit kvíz</h1>
                <p className="page-subtitle">Upravte existující kvíz</p>

                <form className="quiz-editor-form-container" onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label className="form-label required">Název kvízu</label>
                        <input
                            type="text"
                            className="form-input"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Zadejte název kvízu"
                        />
                    </div>

                    <div className="form-group">
                        <label className="form-label required">Krátký popis</label>
                        <textarea
                            className="form-textarea"
                            rows={3}
                            value={shortDescription}
                            onChange={(e) => setShortDescription(e.target.value)}
                            placeholder="Stručný popis kvízu (zobrazí se v náhledu)"
                        />
                    </div>

                    <div className="form-group">
                        <label className="form-label">Popis</label>
                        <textarea
                            className="form-textarea"
                            rows={5}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Podrobný popis kvízu"
                        />
                    </div>

                    <div className="form-group">
                        <label className="form-label">Tagy</label>
                        <input
                            type="text"
                            className="form-input"
                            value={tagInput}
                            onChange={(e) => setTagInput(e.target.value)}
                            onKeyDown={handleTagKeyDown}
                            placeholder="Zadejte tag a stiskněte Enter"
                        />
                        {tags.length > 0 && (
                            <div className="tags-container">
                                {tags.map(tag => (
                                    <span key={tag} className="tag-chip">
                                        {tag}
                                        <button
                                            type="button"
                                            className="tag-remove"
                                            onClick={() => removeTag(tag)}
                                        >
                                            ×
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="form-group">
                        <label className="form-label">Otázky</label>

                        <button
                            type="button"
                            className="btn-create-question"
                            onClick={() => setShowCreateQuestion(true)}
                        >
                            + Vytvořit novou otázku
                        </button>

                        {selectedQuestions.length > 0 && (
                            <div className="selected-questions-section">
                                <p className="selected-questions-label">
                                    Vybrané otázky ({selectedQuestions.length})
                                </p>
                                <div className="selected-questions-list">
                                    {selectedQuestions.map(q => (
                                        <div key={q.id} className="selected-question-chip">
                                            <span className="selected-question-text">{q.title}</span>
                                            <button
                                                type="button"
                                                className="tag-remove"
                                                onClick={() => removeSelectedQuestion(q.id)}
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {selectedEntityFilters.length > 0 && (
                            <div className="quiz-entity-tags">
                                {selectedEntityFilters.map((item, index) => (
                                    <span key={`${item.type}-${item.data?.id || 'unlinked'}`} className={`tag-chip ${item.type === 'book' ? 'tag-chip-book' : item.type === 'unlinked' ? 'tag-chip-theory' : ''}`}>
                                        {item.type === 'book' ? item.data.bookTitle : item.type === 'unlinked' ? 'Literární teorie' : item.data.name}
                                        <button
                                            type="button"
                                            className="tag-remove"
                                            onClick={() => handleRemoveEntityFilter(index)}
                                        >
                                            ×
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}

                        <div className="quiz-entity-search-wrapper">
                            <input
                                type="text"
                                className="form-input"
                                value={entitySearchTerm}
                                onChange={(e) => setEntitySearchTerm(e.target.value)}
                                placeholder="Vyhledat dle autora nebo knihy..."
                            />
                            {entitySearching && <p className="form-hint">Hledám...</p>}
                            {(entitySearchResults.books.length > 0 || entitySearchResults.authors.length > 0 || entitySearchResults.showUnlinked) && (
                                <div className="quiz-entity-search-results">
                                    {entitySearchResults.showUnlinked && (
                                        <div
                                            className="quiz-entity-result-item"
                                            onClick={handleSelectUnlinked}
                                        >
                                            <span className="result-title">Literární teorie</span>
                                            <span className="result-subtitle">Otázky o literární teorii</span>
                                        </div>
                                    )}
                                    {entitySearchResults.books.length > 0 && (
                                        <>
                                            <div className="quiz-entity-section-header">Knihy</div>
                                            {entitySearchResults.books.map(book => (
                                                <div
                                                    key={`book-${book.id}`}
                                                    className="quiz-entity-result-item"
                                                    onClick={() => handleSelectBook(book)}
                                                >
                                                    <span className="result-title">{book.bookTitle}</span>
                                                    {book.author?.name && (
                                                        <span className="result-subtitle">{book.author.name}</span>
                                                    )}
                                                </div>
                                            ))}
                                        </>
                                    )}
                                    {entitySearchResults.authors.length > 0 && (
                                        <>
                                            <div className="quiz-entity-section-header">Autoři</div>
                                            {entitySearchResults.authors.map(author => (
                                                <div
                                                    key={`author-${author.id}`}
                                                    className="quiz-entity-result-item"
                                                    onClick={() => handleSelectAuthor(author)}
                                                >
                                                    <span className="result-title">{author.name}</span>
                                                </div>
                                            ))}
                                        </>
                                    )}
                                </div>
                            )}
                        </div>

                        {searchLoading && (
                            <p className="form-hint">Načítání otázek...</p>
                        )}

                        {!searchLoading && selectedEntityFilters.length > 0 && searchResults.length === 0 && (
                            <p className="form-hint">Žádné otázky neodpovídají zvoleným filtrům.</p>
                        )}

                        {searchResults.length > 0 && (
                            <div className="question-search-results">
                                {searchResults.map(q => {
                                    const isSelected = selectedQuestions.some(sq => sq.id === q.id);
                                    return (
                                        <div
                                            key={q.id}
                                            className={`question-search-item ${isSelected ? 'selected' : ''}`}
                                            onClick={() => toggleQuestion(q)}
                                        >
                                            <div className="question-search-item-info">
                                                {q.questionType && (
                                                    <span className="question-search-type-badge">
                                                        {QUESTION_TYPE_LABELS[q.questionType] || q.questionType}
                                                    </span>
                                                )}
                                                <span className="question-search-item-title">{q.title}</span>
                                            </div>
                                            <span className="question-search-check">{isSelected ? '✓' : '+'}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {selectedEntityFilters.length === 0 && (
                            <p className="form-hint">Vyhledejte autora nebo knihu pro přidání dalších otázek</p>
                        )}
                    </div>

                    <div className="form-actions">
                        <button type="submit" className="btn-submit">Uložit změny</button>
                        <button type="button" className="btn-cancel-form" onClick={handleCancel}>Zrušit</button>
                    </div>
                </form>
            </div>

            {showCreateQuestion && (
                <CreateQuestionModal
                    onQuestionCreated={handleQuestionCreated}
                    onClose={() => setShowCreateQuestion(false)}
                />
            )}
        </div>
    );
}

export default EditQuiz;
